import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  Brain,
  Upload,
  FileSpreadsheet,
  AlertTriangle,
  BarChart3,
  Lightbulb,
  Loader2,
  CheckCircle,
  XCircle,
  LayoutDashboard,
  FileDown,
} from "lucide-react";
import { useReportExport } from "@/hooks/useReportExport";
import { AnalysisScheduler } from "./AnalysisScheduler";
import { useNavigate } from "react-router-dom";
import { parseUserFile, ACCEPTED_FILE_TYPES, UNSUPPORTED_MESSAGE } from "@/lib/file-parsing";

/** Rows sent over the wire for profiling; the true total is reported separately. */
const MAX_ANALYSIS_ROWS = 2000;

/** supabase.functions.invoke discards non-2xx bodies — dig the real message out. */
async function readFunctionError(error: unknown): Promise<string> {
  const anyErr = error as { context?: Response; message?: string };
  const res = anyErr?.context;
  try {
    if (res && typeof res.clone === "function") {
      const body = await res.clone().json().catch(() => null);
      if (body?.error) return String(body.error);
      const text = await res.clone().text().catch(() => "");
      if (text) return text.slice(0, 300);
    }
  } catch {
    /* fall through to status-based messages */
  }
  if (res?.status === 401) return "Your session expired. Please sign in again and retry.";
  if (res?.status === 413) return "That file is too large. Try a smaller file or fewer rows.";
  if (res && res.status >= 500) return "The analysis service had a problem. Please try again in a moment.";
  return anyErr?.message || "We couldn't analyse this data.";
}


type SchemaItem = {
  column: string;
  detected_type: string;
  null_count: number;
  unique_count: number;
  sample_values: string[];
};

type CleaningRec = {
  issue: string;
  column: string;
  severity: "high" | "medium" | "low";
  action: string;
};

type Insight = {
  title: string;
  description: string;
  type: string;
  confidence: number;
};

type AnalysisResult = {
  schema_analysis: SchemaItem[];
  cleaning_recommendations: CleaningRec[];
  statistics: {
    total_rows: number;
    total_columns: number;
    completeness_score: number;
    summary: Record<string, unknown>;
  };
  insights: Insight[];
  natural_language_summary: string;
};

export const UniversalDataAnalyzer = () => {
  const navigate = useNavigate();
  const [rawData, setRawData] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [analysedRows, setAnalysedRows] = useState<Record<string, unknown>[]>([]);
  const { generateReport, isGenerating } = useReportExport();

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    try {
      const parsed = await parseUserFile(file);
      if (parsed.kind === "table" && parsed.rows?.length) {
        setRawData(JSON.stringify(parsed.rows, null, 2));
        toast.success(`Loaded ${parsed.rows.length} rows from ${file.name}`);
      } else if (parsed.text) {
        setRawData(parsed.text);
        toast.info("Loaded text content — paste or edit a JSON array to analyse it as a table");
      } else {
        toast.error("No rows found in this file");
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : UNSUPPORTED_MESSAGE);
    } finally {
      e.target.value = "";
    }
  };

  const runAnalysis = async () => {
    let rows: Record<string, unknown>[];
    try {
      rows = JSON.parse(rawData);
      if (!Array.isArray(rows) || rows.length === 0) throw new Error();
    } catch {
      toast.error("Upload a CSV or Excel file, or paste your data as a list of records.");
      return;
    }

    setIsAnalyzing(true);
    try {
      // A stale token is the most common cause of an opaque non-2xx here.
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        toast.error("Your session expired. Please sign in again and retry.");
        setIsAnalyzing(false);
        return;
      }


      // Check AI quota before proceeding (the function derives the user from the JWT)
      const { data: quotaData } = await supabase.functions.invoke("check-quota", {
        body: { quotaType: "ai_call" },
      });

      if (quotaData && !quotaData.allowed) {
        toast.error(`Daily AI analysis limit reached (${quotaData.limit}). Resets in 24h.`);
        setIsAnalyzing(false);
        return;
      }

      if (quotaData?.remaining <= 5) {
        toast.info(`${quotaData.remaining} AI analyses remaining today`);
      }

      // Cap the payload: profile a bounded sample, report the true total.
      const totalRows = rows.length;
      const sample = rows.slice(0, MAX_ANALYSIS_ROWS);
      if (totalRows > MAX_ANALYSIS_ROWS) {
        toast.info(`Analysing the first ${MAX_ANALYSIS_ROWS} of ${totalRows} rows`);
      }

      const { data, error } = await supabase.functions.invoke("ai-data-processor", {
        body: {
          rows: sample,
          total_rows: totalRows,
          source_type: "manual",
          source_name: fileName || "Pasted Data",
        },
      });

      if (error) throw new Error(await readFunctionError(error));
      if (!data?.success) throw new Error(data?.error || "Analysis failed");

      setResult(data.analysis);
      setAnalysedRows(rows);
      toast.success("Analysis complete!");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Analysis failed";
      toast.error(message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const severityColor = (s: string) => {
    if (s === "high") return "destructive";
    if (s === "medium") return "secondary";
    return "outline";
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Analyse a file
          </CardTitle>
          <CardDescription>
            Upload a spreadsheet and we'll tell you what's in it, what needs fixing, and what stands out. No setup needed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-center">
            <label className="cursor-pointer">
              <input type="file" accept={ACCEPTED_FILE_TYPES} className="hidden" onChange={handleFileUpload} />
              <div className="flex items-center gap-2 px-4 py-2 rounded-md border border-input bg-background hover:bg-accent transition-colors">
                <Upload className="h-4 w-4" />
                <span className="text-sm">Choose a file</span>
              </div>
            </label>
            {fileName && (
              <Badge variant="secondary">
                <FileSpreadsheet className="h-3 w-3 mr-1" />
                {fileName}
              </Badge>
            )}
          </div>

          <Textarea
            placeholder='Paste JSON array here, e.g.: [{"name":"John","age":30,"city":"NYC"}, ...]'
            value={rawData}
            onChange={(e) => setRawData(e.target.value)}
            rows={6}
            className="font-mono text-xs"
          />

          <Button onClick={runAnalysis} disabled={isAnalyzing || !rawData.trim()}>
            {isAnalyzing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Reading your data…
              </>
            ) : (
              <>
                <Brain className="h-4 w-4 mr-2" />
                Analyse my data
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Tabs defaultValue="summary" className="space-y-4">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="summary">Overview</TabsTrigger>
            <TabsTrigger value="schema">Columns</TabsTrigger>
            <TabsTrigger value="cleaning">Fixes</TabsTrigger>
            <TabsTrigger value="insights">What stands out</TabsTrigger>
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
          </TabsList>

          {/* Summary */}
          <TabsContent value="summary">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" /> Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 rounded-lg bg-muted">
                    <p className="text-2xl font-bold">{result.statistics.total_rows}</p>
                    <p className="text-sm text-muted-foreground">Rows</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-muted">
                    <p className="text-2xl font-bold">{result.statistics.total_columns}</p>
                    <p className="text-sm text-muted-foreground">Columns</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-muted">
                    <p className="text-2xl font-bold">
                      {Math.round((result.statistics.completeness_score || 0) * 100)}%
                    </p>
                    <p className="text-sm text-muted-foreground">Completeness</p>
                  </div>
                </div>
                <Progress value={(result.statistics.completeness_score || 0) * 100} />
                <p className="text-muted-foreground leading-relaxed">{result.natural_language_summary}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button onClick={() => navigate("/dashboards")}>
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    Generate Visual Dashboard
                  </Button>
                  <Button
                    variant="outline"
                    disabled={isGenerating || !analysedRows.length}
                    onClick={() =>
                      generateReport({
                        title: "Data analysis report",
                        datasetName: fileName || "Analysed data",
                        rows: analysedRows,
                        totalRows: result.statistics.total_rows,
                        context: result.natural_language_summary,
                        extraMetrics: [
                          {
                            label: "Issues flagged",
                            value: String(result.cleaning_recommendations.length),
                          },
                          {
                            label: "Insights found",
                            value: String(result.insights.length),
                          },
                        ],
                      })
                    }
                  >
                    {isGenerating ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileDown className="h-4 w-4 mr-2" />
                    )}
                    {isGenerating ? "Building report…" : "Download as PDF"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schema */}
          <TabsContent value="schema">
            <Card>
              <CardHeader>
                <CardTitle>What each column contains</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {result.schema_analysis.map((col, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{col.detected_type}</Badge>
                        <span className="font-medium">{col.column}</span>
                      </div>
                      <div className="flex gap-4 text-sm text-muted-foreground">
                        <span>{col.null_count} nulls</span>
                        <span>{col.unique_count} unique</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cleaning */}
          <TabsContent value="cleaning">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-primary" />
                  Things worth fixing
                </CardTitle>
              </CardHeader>
              <CardContent>
                {result.cleaning_recommendations.length === 0 ? (
                  <div className="flex items-center gap-2 text-muted-foreground py-4">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    No issues detected — your data looks clean!
                  </div>
                ) : (
                  <div className="space-y-3">
                    {result.cleaning_recommendations.map((rec, i) => (
                      <div key={i} className="p-3 rounded-lg border space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant={severityColor(rec.severity)}>{rec.severity}</Badge>
                          <span className="font-medium">{rec.issue}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Column: <span className="font-mono">{rec.column}</span> — {rec.action}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Insights */}
          <TabsContent value="insights">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-primary" />
                  What stands out
                </CardTitle>
              </CardHeader>
              <CardContent>
                {result.insights.length === 0 ? (
                  <p className="text-muted-foreground py-4">
                    <XCircle className="inline h-4 w-4 mr-1" />
                    Not enough data to generate insights. Try providing more rows.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {result.insights.map((ins, i) => (
                      <div key={i} className="p-4 rounded-lg border space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold">{ins.title}</h4>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{ins.type}</Badge>
                            <span className="text-xs text-muted-foreground">
                              {Math.round((ins.confidence || 0) * 100)}% confidence
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">{ins.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule */}
          <TabsContent value="schedule">
            <AnalysisScheduler sourceName={fileName || "Manual Data"} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};
